import { useQuery } from '@apollo/client';
import { Stack, Tabs } from "@mantine/core";
import React, { useState, useEffect } from "react";
import useWebSocket from 'react-use-websocket';
import { CLOSED_ORDER_USER_QUERY, OPEN_ASK_ORDER_USER_QUERY, OPEN_BID_ORDER_USER_QUERY } from '../../../queries/OrdersAndWallets';
import { getOrderHandler } from '../../../services/orderHandlers';
import { buildWsUrl } from '../../../config/endpoints';
import ClosedOrdersTableGenerator from '../TableGenerator/ClosedOrdersTableGenerator';
import OpenOrdersTableGenerator from '../TableGenerator/OpenOrdersTableGenerator';
import './UserOrdersComponent.css';

// Normalize whatever the API hands us into an epoch-ms number. DB timestamps
// are TIMESTAMP WITHOUT TIME ZONE stored as UTC, and arrive as one of:
//   - pure epoch-ms string  "1750001583000"
//   - ISO with explicit zone "2026-06-15T03:33:03.570Z" / "...+00:00"
//   - naive ISO / "YYYY-MM-DD HH:MM:SS"  → must be treated as UTC, not local
// The table formats with timeZone:"Asia/Singapore" so as long as we hand it
// the right epoch, display is correct.
const toEpochMs = (raw) => {
    if (raw == null || raw === "") return null;
    if (typeof raw === "number") return raw;
    const s = String(raw).trim();
    if (/^\d+$/.test(s)) return parseInt(s, 10);
    if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(s)) return Date.parse(s);
    // Naive timestamp from Postgres — append Z so JS treats it as UTC.
    return Date.parse(s.replace(" ", "T") + "Z");
};

const UserOrdersComponent = ({ symbol, owner, closedOrders, setClosedOrders, openAskOrders, setOpenAskOrders, openBidOrders, setOpenBidOrders }) => {

  const { loading: closeQueryloading, error: closeQueryError, data: closeQueryData } = useQuery(CLOSED_ORDER_USER_QUERY, { variables: { symbol, owner } });
  const { loading: openAskQueryloading, error: openAskQueryError, data: openAskQueryData } = useQuery(OPEN_ASK_ORDER_USER_QUERY, { variables: { symbol, owner } });
  const { loading: openBidQueryloading, error: openBidQueryError, data: openBidQueryData } = useQuery(OPEN_BID_ORDER_USER_QUERY, { variables: { symbol, owner } });
  const { lastMessage } = useWebSocket(buildWsUrl('/orders'), {
    onOpen: () => console.log('WebSocket connection opened'),
    shouldReconnect: () => true,
  });
  
  useEffect(() => {
    console.log("last message:", lastMessage)
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data);
      if (data.userID === owner && data.symbol.toLowerCase() === symbol.toLowerCase() && data.type === 'ORDER_FILLED') {
        getOrderHandler(owner, symbol, setOpenAskOrders, setOpenBidOrders, setClosedOrders);
      }
    }
  }, [lastMessage, owner, symbol, setOpenAskOrders, setOpenBidOrders, setClosedOrders]);

  useEffect(() => {
    try {
      if (closeQueryData !== undefined && closeQueryData.getClosedOrdersForSymbolAndUser) {
        const res = closeQueryData.getClosedOrdersForSymbolAndUser;
        const formattedData = res.map(d => ({
          ...d,
          createdat: toEpochMs(d.createdat),
          filledat: toEpochMs(d.filledat),
        }));
        setClosedOrders(formattedData);
      }
    } catch (error) { }
  }, [symbol, closeQueryloading, closeQueryError, closeQueryData, setClosedOrders]);

  useEffect(() => {
    try {
      if (openAskQueryData !== undefined && openAskQueryData.getOpenAskOrdersForSymbolAndUser) {
        const res = openAskQueryData.getOpenAskOrdersForSymbolAndUser;
        const formattedData = res.map(d => ({
          ...d,
          createdat: toEpochMs(d.createdat),
          updatedat: toEpochMs(d.updatedat),
        }));
        setOpenAskOrders(formattedData);
      }
    } catch (error) { }
  }, [symbol, openAskQueryloading, openAskQueryError, openAskQueryData, setOpenAskOrders]);

  useEffect(() => {
    try {
      if (openBidQueryData !== undefined && openBidQueryData.getOpenBidOrdersForSymbolAndUser) {
        const res = openBidQueryData.getOpenBidOrdersForSymbolAndUser;
        const formattedData = res.map(d => ({
          ...d,
          createdat: toEpochMs(d.createdat),
          updatedat: toEpochMs(d.updatedat),
        }));
        setOpenBidOrders(formattedData);
      }
    } catch (error) { }
  }, [symbol, openBidQueryloading, openBidQueryError, openBidQueryData, setOpenBidOrders]);

  useEffect(() => {
    getOrderHandler(owner, symbol, setOpenAskOrders, setOpenBidOrders, setClosedOrders);
  }, [owner, symbol, setOpenAskOrders, setOpenBidOrders, setClosedOrders]);

  const [activeTab, setActiveTab] = useState("closed");

  return (
    <div className="outer-container">
      <Stack sx={{ overflowX: "auto" }}>
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="closed">Closed Orders {closedOrders.length > 0 && `(${closedOrders.length})`}</Tabs.Tab>
            <Tabs.Tab value="ask">Open Ask {openAskOrders.length > 0 && `(${openAskOrders.length})`}</Tabs.Tab>
            <Tabs.Tab value="bid">Open Bid {openBidOrders.length > 0 && `(${openBidOrders.length})`}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="closed" pt="xs">
            {closedOrders.length > 0
              ? <ClosedOrdersTableGenerator DataObject={closedOrders} title={"CLOSED ORDERS"} />
              : <p className="placeholder">No closed orders</p>}
          </Tabs.Panel>

          <Tabs.Panel value="ask" pt="xs">
            {openAskOrders.length > 0
              ? <OpenOrdersTableGenerator owner={owner} DataObject={openAskOrders} setAsks={setOpenAskOrders} setBids={setOpenBidOrders} setClosed={setClosedOrders} title={"OPEN ASK ORDERS"} symbol={symbol} side={'SELL'} />
              : <p className="placeholder">No open ask orders</p>}
          </Tabs.Panel>

          <Tabs.Panel value="bid" pt="xs">
            {openBidOrders.length > 0
              ? <OpenOrdersTableGenerator owner={owner} DataObject={openBidOrders} setAsks={setOpenAskOrders} setBids={setOpenBidOrders} setClosed={setClosedOrders} title={"OPEN BID ORDERS"} symbol={symbol} side={'BUY'} />
              : <p className="placeholder">No open bid orders</p>}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </div>
  );
};

export default UserOrdersComponent;